import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { useTenant } from "@/contexts/TenantContext";
import { useCatalogUnifiedSearchQuery } from "@/hooks/useCatalogUnifiedSearchQuery";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { ConfirmDialog, useConfirmDialog } from "@/components/ui/confirm-dialog";
import {
  Search,
  Plus,
  BookOpen,
  FileText,
  Edit2,
  Trash2,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Grid3X3,
  List,
  Scan,
  Upload,
  FileSpreadsheet,
  PenTool,
  Library,
  Film,
  Music,
  BookMarked,
  FileIcon,
  Eye,
  MoreHorizontal,
  X,
  SlidersHorizontal,
  Layers,
  MapPin,
  Tag,
  Calendar,
  Building2,
  User,
  ExternalLink,
  Copy,
  BookText,
  ArrowRight,
  Sparkles,
  Zap,
  ShieldOff,
  Newspaper,
} from "lucide-react";
import {
  catalogApi,
  UnifiedSearchResult,
  type CatalogPageFilters,
  type UnifiedSearchResponse,
} from "@/lib/api/catalog";
import { cn } from "@/lib/utils";
import { formatNumberWithCommas } from "@/lib/formatters";
import { useAccessControl } from "@/hooks/useAccessControl";
import { isGlobalSuperAdmin } from "@/lib/permissions";

// Resource type icons mapping
const getResourceTypeIcon = (type: string, fileType?: string) => {
  if (type === "physical") {
    return <BookOpen className="w-5 h-5" />;
  }
  switch (fileType?.toUpperCase()) {
    case "PDF":
      return <FileText className="w-5 h-5" />;
    case "VIDEO":
      return <Film className="w-5 h-5" />;
    case "AUDIO":
      return <Music className="w-5 h-5" />;
    case "EBOOK":
      return <BookMarked className="w-5 h-5" />;
    default:
      return <FileIcon className="w-5 h-5" />;
  }
};

// Resource type colors
const isPaidDigitalResource = (resource: UnifiedSearchResult) =>
  resource.type === "digital" && (resource.pricingRuleCount ?? 0) > 0;

const getResourceTypeColor = (type: string, fileType?: string) => {
  if (type === "physical") {
    return "bg-primary/10 text-primary border-primary/20";
  }
  switch (fileType?.toUpperCase()) {
    case "PDF":
      return "bg-red-100 text-red-700 border-red-200";
    case "VIDEO":
      return "bg-purple-100 text-purple-700 border-purple-200";
    case "AUDIO":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "EBOOK":
      return "bg-green-100 text-green-700 border-green-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export function CatalogManagementPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { tenant, isLoading: tenantLoading, isMainPlatform } = useTenant();
  const toast = useToast();
  const { hasPermission } = useAccessControl();
  const canCreate = hasPermission("catalog.create") || hasPermission("book.create");
  const canEdit =
    hasPermission("catalog.edit") || hasPermission("book.edit") || hasPermission("digital.edit");
  const canDelete =
    hasPermission("catalog.delete") ||
    hasPermission("book.delete") ||
    hasPermission("digital.delete");
  const canBulkImport = hasPermission("catalog.bulkImport");
  const canUploadDigital = hasPermission("digital.upload");
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchParams.get("q") || "");
  const [resourceType, setResourceType] = useState<"all" | "physical" | "digital">(
    (searchParams.get("type") as any) || "all"
  );
  const [pageFilters, setPageFilters] = useState<CatalogPageFilters | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<string>(
    searchParams.get("collection") || "all"
  );
  const [selectedItemType, setSelectedItemType] = useState<string>(
    searchParams.get("itemType") || "all"
  );
  const [selectedBranch, setSelectedBranch] = useState<string>(searchParams.get("branch") || "all");
  const initialLoadDoneRef = useRef(false);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1"));
  const [pageSize, setPageSize] = useState(parseInt(searchParams.get("pageSize") || "12"));
  const [viewMode, setViewMode] = useState<"grid" | "list">(
    (searchParams.get("view") as any) || "grid"
  );
  const [showFilters, setShowFilters] = useState(false);
  const [selectedResource, setSelectedResource] = useState<UnifiedSearchResult | null>(null);
  const [stats, setStats] = useState({
    totalPhysical: 0,
    totalDigital: 0,
    recentlyAdded: 0,
  });
  const [statsLoaded, setStatsLoaded] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstSearchDebounce = useRef(true);

  const canAccessCatalog =
    isGlobalSuperAdmin(user) ||
    user?.isStaff ||
    hasPermission("catalog.view") ||
    hasPermission("catalog.list") ||
    user?.memberships?.some((m) => m.roleName === "Tenant Admin" || m.roleName === "Librarian");

  /** Context tenant can lag behind auth after refresh; API still resolves tenant via x-tenant-slug + user. */
  const catalogTenantKey = useMemo(() => {
    if (typeof window === "undefined") return tenant?.id;
    const sessionSlug = sessionStorage.getItem("tenant_slug");
    return (
      tenant?.id ??
      user?.memberships?.[0]?.tenantId ??
      user?.tenantId ??
      (sessionSlug ? `slug:${sessionSlug}` : undefined)
    );
  }, [tenant?.id, user?.memberships, user?.tenantId]);

  const { confirm, ConfirmDialog: ConfirmDialogComponent, isOpen, setIsOpen } = useConfirmDialog();
  const queryClient = useQueryClient();

  const [pageCursors, setPageCursors] = useState<Record<number, string>>({});
  const cursorForPage = pageCursors[page] ?? undefined;

  const {
    data: searchData,
    isLoading: searchIsLoading,
    isFetching: searchIsFetching,
    isError: searchIsError,
    error: searchError,
  } = useCatalogUnifiedSearchQuery({
    tenantId: catalogTenantKey,
    enabled: canAccessCatalog,
    debouncedQuery: debouncedSearchQuery,
    resourceType,
    selectedCollection,
    selectedItemType,
    selectedBranch,
    page,
    pageSize,
    pageCursors,
    initialLoadDoneRef,
  });

  const resources: UnifiedSearchResult[] = searchData?.data ?? [];
  const total = searchData?.total ?? 0;

  const catalogListBlockingLoad = searchIsLoading && !searchData;
  const statsBlockingLoad = !statsLoaded && !searchData && searchIsLoading;

  useEffect(() => {
    if (searchIsError && searchError) {
      const err = searchError as { name?: string; response?: { data?: { message?: string } } };
      if (err?.name === "AbortError" || (err as { code?: string }).code === "ERR_CANCELED") return;
      toast.error(err.response?.data?.message || "Failed to load catalog resources");
    }
  }, [searchIsError, searchError, toast]);

  useEffect(() => {
    if (!searchData) return;
    if (page === 1) {
      const c: Record<number, string> = {};
      if (searchData.nextCursor && resourceType !== "all") {
        c[2] = searchData.nextCursor;
      }
      setPageCursors(c);
    } else if (searchData.nextCursor && resourceType !== "all") {
      setPageCursors((prev) => ({ ...prev, [page + 1]: searchData.nextCursor! }));
    }
    if (!initialLoadDoneRef.current) {
      if (searchData.stats) {
        setStats({
          totalPhysical: searchData.stats.totalPhysical,
          totalDigital: searchData.stats.totalDigital,
          recentlyAdded: Math.min(
            searchData.stats.totalPhysical + searchData.stats.totalDigital,
            15
          ),
        });
        setStatsLoaded(true);
      }
      if (searchData.filters) {
        setPageFilters(searchData.filters);
      }
      initialLoadDoneRef.current = true;
    }
  }, [searchData, page, resourceType]);

  useEffect(() => {
    if (!catalogTenantKey) return;
    initialLoadDoneRef.current = false;
    setStatsLoaded(false);
  }, [catalogTenantKey]);

  const collections = pageFilters?.collections ?? [];
  const itemTypes = pageFilters?.itemTypes ?? [];
  const branches = pageFilters?.branches ?? [];

  // Debounce search (do not reset page on first run after mount — avoids fighting URL + cursor state)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      if (isFirstSearchDebounce.current) {
        isFirstSearchDebounce.current = false;
      } else {
        setPage(1);
      }
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  useLayoutEffect(() => {
    if (resourceType === "all" || page <= 1) return;
    if (!cursorForPage) {
      setPage(1);
    }
  }, [resourceType, page, cursorForPage]);

  useEffect(() => {
    updateSearchParams();
  }, [
    debouncedSearchQuery,
    resourceType,
    selectedCollection,
    selectedItemType,
    selectedBranch,
    page,
    pageSize,
    viewMode,
  ]);

  const updateSearchParams = () => {
    const params = new URLSearchParams();
    if (debouncedSearchQuery) params.set("q", debouncedSearchQuery);
    if (resourceType !== "all") params.set("type", resourceType);
    if (selectedCollection !== "all") params.set("collection", selectedCollection);
    if (selectedItemType !== "all") params.set("itemType", selectedItemType);
    if (selectedBranch !== "all") params.set("branch", selectedBranch);
    if (page > 1) params.set("page", page.toString());
    if (pageSize !== 12) params.set("pageSize", pageSize.toString());
    if (viewMode !== "grid") params.set("view", viewMode);
    setSearchParams(params);
  };

  const handleDelete = async () => {
    if (!selectedResource) return;
    const removedId = selectedResource.id;
    const removedType = selectedResource.type;

    try {
      if (selectedResource.type === "digital") {
        await catalogApi.deleteDigitalResource(selectedResource.id);
      } else {
        await catalogApi.deleteBiblio(selectedResource.id);
      }

      if (catalogTenantKey) {
        queryClient.setQueriesData<UnifiedSearchResponse | undefined>(
          ["catalog", "unified", catalogTenantKey],
          (old) => {
            if (!old?.data) return old;
            const data = old.data.filter((r) => !(r.id === removedId && r.type === removedType));
            return { ...old, data, total: Math.max(0, (old.total ?? 0) - 1) };
          }
        );
        await queryClient.invalidateQueries(["catalog", "unified", catalogTenantKey]);
      }
      toast.success("Resource deleted successfully!");
      setSelectedResource(null);
      setStats((prev) =>
        selectedResource.type === "physical"
          ? { ...prev, totalPhysical: Math.max(0, prev.totalPhysical - 1) }
          : { ...prev, totalDigital: Math.max(0, prev.totalDigital - 1) }
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to delete resource");
    }
  };

  const handleDeleteClick = (resource: UnifiedSearchResult) => {
    setSelectedResource(resource);
    confirm({
      title: "Delete Resource",
      description: (
        <div className="space-y-2">
          <p>
            Are you sure you want to delete <strong>"{resource.title}"</strong>?
          </p>
          <p className="text-sm text-gray-500">This action cannot be undone.</p>
        </div>
      ),
      variant: "danger",
      confirmLabel: "Delete",
      onConfirm: handleDelete,
    });
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return null;
    const mb = bytes / (1024 * 1024);
    if (mb < 1) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${mb.toFixed(1)} MB`;
  };

  const clearFilters = () => {
    setResourceType("all");
    setSelectedCollection("all");
    setSelectedItemType("all");
    setSelectedBranch("all");
    setSearchQuery("");
    setPage(1);
  };

  const hasActiveFilters =
    resourceType !== "all" ||
    selectedCollection !== "all" ||
    selectedItemType !== "all" ||
    selectedBranch !== "all" ||
    searchQuery !== "";

  const totalPages = Math.ceil(total / pageSize);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" label="Loading session…" />
      </div>
    );
  }

  if (!canAccessCatalog) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
        <Card className="max-w-md p-8 text-center">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
            <ShieldOff className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
          <p className="text-gray-600">
            You need administrator or librarian permissions to access the catalog.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Library Catalog"
        description="Browse, search, and manage your library's physical and digital resources."
        breadcrumbItems={[{ label: "Catalog" }]}
        showSearch={false}
      />

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card
          className="border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setResourceType("physical")}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <div>
                {statsBlockingLoad ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumberWithCommas(stats.totalPhysical)}
                    </p>
                    <p className="text-sm text-gray-500">Physical Items</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => setResourceType("digital")}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg">
                <FileText className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                {statsBlockingLoad ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumberWithCommas(stats.totalDigital)}
                    </p>
                    <p className="text-sm text-gray-500">Digital Resources</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card
          className="border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => {
            setResourceType("all");
            clearFilters();
          }}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                <Library className="w-6 h-6 text-green-600" />
              </div>
              <div>
                {statsBlockingLoad ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatNumberWithCommas(stats.totalPhysical + stats.totalDigital)}
                    </p>
                    <p className="text-sm text-gray-500">Total Resources</p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 cursor-pointer hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-amber-100 rounded-lg">
                <Sparkles className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumberWithCommas(collections.length)}
                </p>
                <p className="text-sm text-gray-500">Collections</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions — only show if user can create */}
      {canCreate && (
        <Card className="mb-6 border border-gray-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Quick Actions — Add to Catalog
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-start gap-2 hover:bg-primary/5 hover:border-primary/30 group"
                onClick={() => navigate("/catalog/isbn")}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                    <Scan className="w-5 h-5 text-primary" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">ISBN Scan</p>
                    <p className="text-xs text-gray-500">Auto-catalog by ISBN</p>
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-start gap-2 hover:bg-green-50 hover:border-green-300 group"
                onClick={() => navigate("/catalog/manual")}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                    <PenTool className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Manual Entry</p>
                    <p className="text-xs text-gray-500">Enter book details</p>
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-start gap-2 hover:bg-teal-50 hover:border-teal-300 group"
                onClick={() => navigate("/catalog/journal")}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex items-center justify-center w-10 h-10 bg-teal-100 rounded-lg group-hover:bg-teal-200 transition-colors">
                    <Newspaper className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Catalog Journal</p>
                    <p className="text-xs text-gray-500">Add journal/serial</p>
                  </div>
                </div>
              </Button>

              {(canCreate || canUploadDigital) && (
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start gap-2 hover:bg-purple-50 hover:border-purple-300 group"
                  onClick={() => navigate("/catalog/upload")}
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                      <Upload className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Upload E-Resource</p>
                      <p className="text-xs text-gray-500">PDF, Video, Audio</p>
                    </div>
                  </div>
                </Button>
              )}

              {canBulkImport && (
                <Button
                  variant="outline"
                  className="h-auto p-4 flex flex-col items-start gap-2 hover:bg-orange-50 hover:border-orange-300 group"
                  onClick={() => navigate("/catalog/bulk-import")}
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex items-center justify-center w-10 h-10 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                      <FileSpreadsheet className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Bulk Import</p>
                      <p className="text-xs text-gray-500">Import from CSV</p>
                    </div>
                  </div>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search and Filters */}
      <Card className="mb-6 border border-gray-200">
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Search Row */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by title, author, ISBN, subject..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-11"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Resource Type Tabs */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => {
                      setResourceType("all");
                      setPage(1);
                    }}
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                      resourceType === "all"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    )}
                  >
                    All
                  </button>
                  <button
                    onClick={() => {
                      setResourceType("physical");
                      setPage(1);
                    }}
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                      resourceType === "physical"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    )}
                  >
                    Physical
                  </button>
                  <button
                    onClick={() => {
                      setResourceType("digital");
                      setPage(1);
                    }}
                    className={cn(
                      "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                      resourceType === "digital"
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    )}
                  >
                    Digital
                  </button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className={cn("gap-2", showFilters && "bg-gray-100")}
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  Filters
                  {hasActiveFilters && (
                    <span className="flex items-center justify-center w-5 h-5 text-xs bg-primary text-primary-foreground rounded-full">
                      {
                        [
                          resourceType !== "all",
                          selectedCollection !== "all",
                          selectedItemType !== "all",
                          selectedBranch !== "all",
                        ].filter(Boolean).length
                      }
                    </span>
                  )}
                </Button>

                <div className="flex items-center border rounded-lg">
                  <button
                    onClick={() => setViewMode("grid")}
                    className={cn(
                      "p-2 rounded-l-lg transition-colors",
                      viewMode === "grid"
                        ? "bg-gray-100 text-gray-900"
                        : "text-gray-400 hover:text-gray-600"
                    )}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={cn(
                      "p-2 rounded-r-lg transition-colors",
                      viewMode === "list"
                        ? "bg-gray-100 text-gray-900"
                        : "text-gray-400 hover:text-gray-600"
                    )}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-gray-100">
                <Select
                  value={selectedCollection}
                  onValueChange={(value) => {
                    setSelectedCollection(value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[180px]">
                    <Layers className="w-4 h-4 mr-2 text-gray-400" />
                    <SelectValue placeholder="Collection" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Collections</SelectItem>
                    {collections.map((collection) => (
                      <SelectItem key={collection.id} value={collection.id}>
                        {collection.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {resourceType !== "digital" && (
                  <Select
                    value={selectedItemType}
                    onValueChange={(value) => {
                      setSelectedItemType(value);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <Tag className="w-4 h-4 mr-2 text-gray-400" />
                      <SelectValue placeholder="Item Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Item Types</SelectItem>
                      {itemTypes.map((itemType) => (
                        <SelectItem key={itemType.id} value={itemType.id}>
                          {itemType.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {resourceType !== "digital" && branches.length > 1 && (
                  <Select
                    value={selectedBranch}
                    onValueChange={(value) => {
                      setSelectedBranch(value);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                      <SelectValue placeholder="Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                          {branch.isMain ? " (Main)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="12">12 per page</SelectItem>
                    <SelectItem value="24">24 per page</SelectItem>
                    <SelectItem value="48">48 per page</SelectItem>
                    <SelectItem value="96">96 per page</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1">
                    <X className="w-3 h-3" />
                    Clear all
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-600">
          {catalogListBlockingLoad ? (
            "Searching…"
          ) : (
            <>
              <span className="font-semibold text-gray-900">{formatNumberWithCommas(total)}</span>{" "}
              {total === 1 ? "resource" : "resources"} found
              {searchIsFetching && (
                <span className="ml-2 text-gray-400" aria-live="polite">
                  (Updating…)
                </span>
              )}
              {debouncedSearchQuery && (
                <>
                  {" "}
                  for &quot;
                  <span className="font-medium">{debouncedSearchQuery}</span>
                  &quot;
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Results */}
      {catalogListBlockingLoad ? (
        <div className="py-16">
          <LoadingSpinner size="lg" label="Loading catalog..." />
        </div>
      ) : resources.length === 0 ? (
        <Card className="border border-gray-200">
          <CardContent className="py-16 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {debouncedSearchQuery ? "No results found" : "Your catalog is empty"}
            </h3>
            <p className="mb-6 text-sm text-gray-500 max-w-md mx-auto">
              {debouncedSearchQuery
                ? "Try adjusting your search terms or filters to find what you're looking for."
                : "Start building your catalog by adding books and digital resources."}
            </p>
            {!debouncedSearchQuery && canCreate && (
              <div className="flex flex-wrap justify-center gap-3">
                <Button onClick={() => navigate("/catalog/isbn")} className="gap-2">
                  <Scan className="w-4 h-4" />
                  Scan ISBN
                </Button>
                <Button variant="outline" onClick={() => navigate("/catalog/manual")}>
                  <PenTool className="w-4 h-4 mr-2" />
                  Manual Entry
                </Button>
              </div>
            )}
            {debouncedSearchQuery && (
              <Button variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === "grid" ? (
        /* Grid View */
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {resources.map((resource) => (
            <Card
              key={resource.id}
              className="group overflow-hidden border border-gray-200 hover:shadow-lg hover:border-gray-300 transition-all cursor-pointer"
              onClick={() => {
                if (resource.type === "physical") {
                  navigate(`/catalog/physical/${resource.id}`);
                } else {
                  navigate(`/catalog/digital/${resource.id}`);
                }
              }}
            >
              {/* Cover Image or Placeholder */}
              <div className="relative h-40 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center overflow-hidden">
                {resource.coverUrl ? (
                  <img
                    src={resource.coverUrl}
                    alt={resource.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div
                    className={cn(
                      "flex items-center justify-center w-16 h-16 rounded-lg",
                      getResourceTypeColor(resource.type, (resource as any).fileType)
                    )}
                  >
                    {getResourceTypeIcon(resource.type, (resource as any).fileType)}
                  </div>
                )}

                {/* Type Badge */}
                <div className="absolute top-2 left-2 flex flex-wrap gap-1 max-w-[70%]">
                  <Badge
                    className={cn(
                      "text-xs border",
                      getResourceTypeColor(resource.type, (resource as any).fileType)
                    )}
                  >
                    {resource.type === "physical"
                      ? "Book"
                      : (resource as any).fileType || "Digital"}
                  </Badge>
                  {isPaidDigitalResource(resource) && (
                    <Badge className="text-xs border bg-amber-100 text-amber-800 border-amber-200">
                      Paid
                    </Badge>
                  )}
                </div>

                {/* Quick Actions */}
                {canEdit && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 w-8 p-0 bg-white/90 hover:bg-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (resource.type === "digital") {
                          navigate(`/catalog/digital/${resource.id}/edit`);
                        } else {
                          navigate(`/catalog/physical/${resource.id}/edit`);
                        }
                      }}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>

              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 line-clamp-2 mb-1 group-hover:text-primary transition-colors">
                  {resource.title}
                </h3>

                {resource.authors && resource.authors.length > 0 && (
                  <p className="text-sm text-gray-500 line-clamp-1 mb-2">
                    {resource.authors.join(", ")}
                  </p>
                )}

                <div className="flex flex-wrap gap-1">
                  {resource.subjects &&
                    resource.subjects.slice(0, 2).map((subject: string, idx: number) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-gray-50 text-gray-600"
                      >
                        {subject}
                      </Badge>
                    ))}
                  {resource.subjects && resource.subjects.length > 2 && (
                    <Badge variant="outline" className="text-xs bg-gray-50 text-gray-600">
                      +{resource.subjects.length - 2}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        /* List View */
        <Card className="border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {resources.map((resource) => (
              <div
                key={resource.id}
                className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer group"
                onClick={() => {
                  if (resource.type === "physical") {
                    navigate(`/catalog/physical/${resource.id}`);
                  } else {
                    navigate(`/catalog/digital/${resource.id}`);
                  }
                }}
              >
                {/* Cover/Icon */}
                <div className="shrink-0 w-16 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                  {resource.coverUrl ? (
                    <img
                      src={resource.coverUrl}
                      alt={resource.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div
                      className={cn(
                        "flex items-center justify-center w-10 h-10 rounded",
                        getResourceTypeColor(resource.type, (resource as any).fileType)
                      )}
                    >
                      {getResourceTypeIcon(resource.type, (resource as any).fileType)}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 line-clamp-1 group-hover:text-primary transition-colors">
                      {resource.title}
                    </h3>
                    <Badge
                      className={cn(
                        "text-xs border shrink-0",
                        getResourceTypeColor(resource.type, (resource as any).fileType)
                      )}
                    >
                      {resource.type === "physical"
                        ? "Book"
                        : (resource as any).fileType || "Digital"}
                    </Badge>
                    {isPaidDigitalResource(resource) && (
                      <Badge className="text-xs border shrink-0 bg-amber-100 text-amber-800 border-amber-200">
                        Paid
                      </Badge>
                    )}
                  </div>

                  {resource.authors && resource.authors.length > 0 && (
                    <p className="text-sm text-gray-500 line-clamp-1 mb-1">
                      by {resource.authors.join(", ")}
                    </p>
                  )}

                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    {resource.publisher && (
                      <span className="flex items-center gap-1">
                        <BookText className="w-3 h-3" />
                        {resource.publisher}
                      </span>
                    )}
                    {resource.type === "digital" && (resource as any).fileSize && (
                      <span>{formatFileSize((resource as any).fileSize)}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {resource.type === "digital" && (resource as any).isPublic && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (tenant?.slug) {
                          sessionStorage.setItem("tenant_slug", tenant.slug);
                        }
                        navigate(`/view/${resource.id}`);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (resource.type === "digital") {
                          navigate(`/catalog/digital/${resource.id}/edit`);
                        } else {
                          navigate(`/catalog/physical/${resource.id}/edit`);
                        }
                      }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(resource);
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex flex-col items-center justify-between gap-4 mt-6 sm:flex-row">
          <div className="text-sm text-gray-600">
            Showing {formatNumberWithCommas((page - 1) * pageSize + 1)} to{" "}
            {formatNumberWithCommas(Math.min(page * pageSize, total))} of{" "}
            {formatNumberWithCommas(total)}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="h-9 w-9 p-0"
            >
              <ChevronsLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-9 w-9 p-0"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>

            {/* Page Numbers */}
            <div className="flex items-center gap-1">
              {(() => {
                const maxVisible = 5;
                let start = Math.max(1, page - Math.floor(maxVisible / 2));
                let end = Math.min(totalPages, start + maxVisible - 1);

                if (end - start < maxVisible - 1) {
                  start = Math.max(1, end - maxVisible + 1);
                }

                const pages: (number | string)[] = [];

                if (start > 1) {
                  pages.push(1);
                  if (start > 2) pages.push("...");
                }

                for (let i = start; i <= end; i++) {
                  pages.push(i);
                }

                if (end < totalPages) {
                  if (end < totalPages - 1) pages.push("...");
                  pages.push(totalPages);
                }

                return pages.map((p, idx) => {
                  if (p === "...") {
                    return (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return (
                    <Button
                      key={p}
                      variant={page === p ? "default" : "outline"}
                      size="sm"
                      onClick={() => setPage(p as number)}
                      className="h-9 min-w-9"
                    >
                      {p}
                    </Button>
                  );
                });
              })()}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="h-9 w-9 p-0"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages}
              className="h-9 w-9 p-0"
            >
              <ChevronsRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialogComponent />
    </div>
  );
}
